// W52.0 D-6 — the topbar avatar opens a menu instead of navigating.
//
// Three items as of W52.1 D-3: 個人主頁 / 配對 / 登出.
//
// The defect was not that the avatar was a link. It was that a link is **one
// destination deep**, and 「登出」 — a real, primary action — lived three
// screens away at `/settings/account`, behind a password card. A person who
// wants to sign out of a shared phone has to go and read a settings page.
//
// The first two keep the route the avatar always went to, so nothing about the
// existing path changes; 登出 is the thing that could not be reached at all.
// 配對 arrived from the nav's submenu in W52.1 — it is the relationship layer,
// which is what this menu now holds.
//
// **Desktop is untouched, on purpose.** `TopBar.tsx` has no avatar and
// `IconRail` already carries 「我」. Adding one there would create a second
// entry to the same place, which is what `ADR-0019` deleted a duplicate of.
//
// Material: white card + hairline + `shadow-pop`, **not** the pill nav's navy.
// The topbar floats on the warm paper ground and a white card reads against it;
// the nav is the navy L3 chrome. Same reasoning as `PageHero` dropping its own
// background (W42 F18): the two are different layers and should not share a
// material.
//
// Mechanics are copied from `PillNavMenu` rather than reinvented, because that
// is where the app's answers to the three real questions already live:
// click-outside on capture-phase mousedown (closes on the *same* gesture),
// `Escape`, and `inert` while closed — `opacity-0 pointer-events-none` leaves
// every row in the tab order and in the a11y tree, which is how a Tab walk of
// the dashboard once measured five invisible focus stops.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, User } from "lucide-react";

import { AuthorAvatar } from "@/components/primitives/AuthorAvatar";
import { logout } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/AuthProvider";
import { renderModuleIcon } from "@/components/ui/navIcon";
import { MOBILE_ACCOUNT_ITEMS } from "@/lib/moduleManifests";
import {
  gapDown,
  gapUp,
  POPOVER_PANEL_CLASS,
  POPOVER_ROW_CLASS,
  POPOVER_TILE_CLASS,
  usePopoverPlacement,
} from "@/components/system/Popover";

export function AvatarMenu({
  avatarSrc,
  avatarLabel,
}: {
  avatarSrc: string | null | undefined;
  avatarLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const { markSignedOut, refresh } = useAuth();

  // W52.3 — the shared placement rule. The avatar sits in the top-right, so
  // the rule says "right" and the panel's right edge meets the trigger's. It
  // used to say the same thing as a hardcoded `right-0`, which is fine until
  // the trigger moves; the rule keeps agreeing with wherever the trigger
  // actually is, and can flip it if the topbar is ever rearranged.
  const placement = usePopoverPlacement(triggerRef, "bottom");

  useEffect(() => {
    if (!open) return;
    // Capture phase: close on the same gesture that dismisses a native popover,
    // not one event later.
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  /**
   * The exact sequence `Account.tsx:133` uses, in the same order.
   *
   * `markSignedOut()` before `refresh()` is load-bearing: without it, "I signed
   * out" and "I was signed out elsewhere" are the same state, and the login
   * page tells someone they were evicted when they pressed the button.
   */
  const handleLogout = async () => {
    setOpen(false);
    await logout();
    markSignedOut();
    await refresh();
    nav("/", { replace: true });
  };

  /**
   * `inert` while closed, set through the ref.
   *
   * `opacity-0 pointer-events-none` — which is how the panel is hidden — leaves
   * every row in the tab order and in the accessibility tree. `PillNav` had
   * this measured: a Tab walk of the dashboard put 5 focus stops on rows the
   * user cannot see. Boolean `inert` is React 19+; this app is on 18.3, so the
   * property is set on the DOM node directly.
   */
  useEffect(() => {
    panelRef.current?.toggleAttribute("inert", !open);
  }, [open]);

  return (
    // `relative` so the panel is anchored to the trigger. A `shrink-0` wrapper
    // would leave the header's flex row with a generated box that eats the
    // gap — the same thing `PillNavMenu` had to undo with `display: contents`.
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="mobile-header-avatar"
        aria-label="帳號選單"
        aria-expanded={open}
        aria-haspopup="menu"
        className="press shell-avatar"
      >
        <AuthorAvatar
          tone="you"
          size="shell"
          label={avatarLabel}
          src={avatarSrc ?? undefined}
          className="bg-chrome-navy text-paper border-white/20"
        />
      </button>

      <div
        ref={panelRef}
        role="menu"
        data-testid="avatar-menu"
        data-open={open ? "true" : undefined}
        data-align={placement.align}
        data-side={placement.side}
        // W52.4 — the gap follows the side, for the same reason the nav popper's
        // does: `marginTop` opens downward, `marginBottom` opens upward.
        style={{ ...placement.style, ...(placement.side === "bottom" ? gapDown() : gapUp()) }}
        className={[
          // W52.3 — navy, `rounded-[20px]`, `shadow-pop`, the same panel every
          // other floating menu uses.
          //
          // **This reverses a W52.0 D-6 decision on purpose.** That entry chose
          // a white card here with the reason 「the topbar floats on the warm
          // paper ground and a white card reads against it; the nav is the navy
          // L3 chrome」. The reasoning is sound in isolation — but it applied it
          // to a **floating panel**, and a panel belongs to the layer it floats
          // in, not the layer behind it. The result was three materials across
          // five popovers on screens one tap apart.
          //
          // The `surface-plain` → `bg-surface-plain` trap recorded below is why
          // this file had a long comment about its own background in the first
          // place; it is kept because the lesson outlives the decision it was
          // written for.
          POPOVER_PANEL_CLASS,
          // **Which edge it hangs from is the shared rule's answer** (W52.4), so
          // `top-full` is the `bottom` branch of it rather than a fact about this
          // component. The avatar is in the top bar and opens downward, which is
          // what the rule says for a trigger with 768px of room beneath it.
          //
          // **`top-full` is load-bearing.** It was dropped while the classes
          // were being swapped for the shared tokens, and an absolutely
          // positioned element with no `top` sits at its *static* position —
          // i.e. wherever it would have landed in flow — which put the measured
          // gap at 21px instead of 16 and, more to the point, decoupled the
          // panel from the trigger entirely.
          //
          // This is the third time this sprint that a class that *positioned*
          // something has been the thing that went missing, after the W52.2
          // anchor and the interpolated `mb-[${POPOVER_GAP}px]`. Position is
          // the part a visual review cannot reconstruct from memory and a class
          // assertion never reads.
          "absolute z-30 min-w-48",
          /* gap via inline style — see `gapDown` */
          placement.side === "bottom"
            ? "top-full origin-top-right"
            : "bottom-full origin-bottom-right",
          "transition-all duration-quick ease-quiet",
          open
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-95 pointer-events-none",
        ].join(" ")}
      >
        <Link
          to="/profile"
          role="menuitem"
          onClick={() => setOpen(false)}
          data-testid="avatar-menu-profile"
          className={POPOVER_ROW_CLASS}
        >
          <span className={POPOVER_TILE_CLASS}>
            <User size={17} aria-hidden />
          </span>
          個人主頁
        </Link>
        {/*
          W52.1 D-3 — 配對 moved here from the nav's submenu, where it sat in a
          content list under a group heading that was left holding one item.

          It is rendered **from the manifest** rather than hardcoded, which is
          the whole point of the `account` surface: `MOBILE_ACCOUNT_ITEMS` is
          filtered from `APP_SHELL_NAV`, so a module reclassified to
          `"account"` shows up here without anyone editing this file, and a
          reclassified module that nobody renders fails the coverage throw in
          `moduleManifests.ts` instead of vanishing from the app.

          This row is also the app's **only** mobile entrance to `/pair` for an
          already-paired user — `Dashboard`'s two `<Link to="/pair">` are both
          inside the `noPair` branch — which is why deleting the submenu row
          required adding this one rather than merely removing a line.
        */}
        {MOBILE_ACCOUNT_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            role="menuitem"
            onClick={() => setOpen(false)}
            data-testid={`avatar-menu-${item.testId.replace(/^nav-/, "")}`}
            className={POPOVER_ROW_CLASS}
          >
            <span className={POPOVER_TILE_CLASS}>
              {renderModuleIcon(item.icon, { size: 17 })}
            </span>
            {item.label}
          </Link>
        ))}
        <button
          type="button"
          role="menuitem"
          onClick={() => void handleLogout()}
          data-testid="avatar-menu-logout"
          className={[
            POPOVER_ROW_CLASS,
            // A destructive row reads as destructive by its **text**, not by a
            // red panel: the panel is shared chrome now, so the tint that would
            // have been a whole-card treatment becomes one row's label.
            "text-partner-strong hover:bg-white/10",
          ].join(" ")}
        >
          <span className="bg-white/10 text-partner-strong flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[11px]">
            <LogOut size={17} aria-hidden />
          </span>
          登出
        </button>
      </div>
    </div>
  );
}
